import type { EntityBase } from "@/models/entity-base";
import { fetchFromBackendGet, type SearchEndpoint } from "@/services/backend";
import type { User } from "@/models/user";
import type { DDC } from "@/composables/search-ddc";
import type { Keyword } from "@/models/keyword";

export type SearchResult = EntityBase | User | DDC | Keyword;

export interface SearchParams extends Record<string, any> {
  start?: number;
  maxRows?: number;
  field?: string[];
  fieldset?: string;
  sortfield?: string[];
  lang?: string;
}

export interface SearchResponse<E extends SearchResult, F> {
  type: string;
  start: number;
  numFound: number;
  maxRows: number;
  lang: "de" | "en";
  docs: E[];
  facets?: F;
}

export interface Facet {}

export async function search<
  P extends SearchParams,
  E extends SearchResult,
  F extends Facet,
>(endpoint: SearchEndpoint, params: P): Promise<SearchResponse<E, F>> {
  const data = await fetchFromBackendGet(endpoint, params);
  return data as SearchResponse<E, F>;
}

export async function getEntriesById<
  P extends SearchParams,
  E extends SearchResult,
>(endpoint: SearchEndpoint, params: P): Promise<Record<number, E>> {
  const searchResponse = await search<P, E, any>(endpoint, params);
  return searchResponse.docs.reduce(
    (p, c) => {
      p[c.id] = c;
      return p;
    },
    {} as Record<number, E>
  );
}

export async function getEntries<
  P extends SearchParams,
  E extends SearchResult,
>(endpoint: SearchEndpoint, params: P): Promise<E[]> {
  const searchResponse = await search<P, E, any>(endpoint, params);
  return searchResponse.docs;
}

export async function getEntry<P extends SearchParams, E extends SearchResult>(
  endpoint: SearchEndpoint,
  params: P
): Promise<E | null> {
  params.maxRows = 1;
  const searchResponse = await search<P, E, any>(endpoint, params);
  return searchResponse.docs?.[0] ?? null;
}
